import { ArrowUpRight, ArrowDownRight, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TrendDirection } from '@/types'

interface TrendIndicatorProps {
  trend: TrendDirection
  changePercent?: number
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

// Hearth: a downward trend is worth a look, not an emergency — warning, never error.
const trendConfig: Record<
  TrendDirection,
  { icon: typeof ArrowUpRight; color: string; bgColor: string; label: string }
> = {
  improving: {
    icon: ArrowUpRight,
    color: 'text-success-600',
    bgColor: 'bg-success-600/10',
    label: 'Improving',
  },
  stable: {
    icon: ArrowRight,
    color: 'text-gray-500',
    bgColor: 'bg-gray-400/10',
    label: 'Steady',
  },
  declining: {
    icon: ArrowDownRight,
    color: 'text-warning-600',
    bgColor: 'bg-warning-600/10',
    label: 'Worth a look',
  },
}

const sizeConfig = {
  sm: { icon: 'h-3 w-3', text: 'text-xs', padding: 'px-1.5 py-0.5' },
  md: { icon: 'h-4 w-4', text: 'text-sm', padding: 'px-2 py-1' },
  lg: { icon: 'h-5 w-5', text: 'text-base', padding: 'px-2.5 py-1.5' },
}

export function TrendIndicator({
  trend,
  changePercent,
  showLabel = false,
  size = 'md',
}: TrendIndicatorProps) {
  const config = trendConfig[trend]
  const sizes = sizeConfig[size]
  const Icon = config.icon

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-full',
        config.bgColor,
        sizes.padding
      )}
    >
      <Icon className={cn(sizes.icon, config.color)} />
      {changePercent !== undefined && (
        <span className={cn(sizes.text, 'font-medium tabular-nums', config.color)}>
          {changePercent > 0 ? '+' : ''}
          {changePercent.toFixed(1)}%
        </span>
      )}
      {showLabel && (
        <span className={cn(sizes.text, 'font-medium', config.color)}>
          {config.label}
        </span>
      )}
    </div>
  )
}
